"use client";

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { StaffTimeTrackingProvider } from "@/context/StaffTimeTrackingContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Car as CarIcon,
  Package,
  LogOut,
  Search,
  Filter,
  CheckSquare,
  Plus,
  Pencil,
} from "lucide-react";
import { splitRemarks } from "@/lib/cancellationRemark";
import { AppointmentStatus, Appointment } from "@/types";
import {
  APPOINTMENT_STATUS_OPTIONS,
  getStatusBadgeClasses,
  isAppointmentStatusLocked,
} from "@/lib/appointmentStatus";
import { toast } from "sonner";
import { cn, formatCurrency, formatDate, formatTime } from "@/lib/utils";
import { format } from "date-fns";
import { TimeTrackingWidget } from "@/components/staff/TimeTrackingWidget";
import { StaffFeatureUsageDialog } from "@/components/staff/StaffFeatureUsageDialog";

const statusOptions = APPOINTMENT_STATUS_OPTIONS;

// Features are returned to the subscription when a job did not happen,
// so they can't be recorded on appointments in these statuses.
const FEATURE_LOCKED_STATUSES: AppointmentStatus[] = [
  "CANCELLED",
  "POSTPONED",
  "NOT_COMPLETED",
];

// Backend caps list requests at 500; the default of 50 would cut off older
// appointments once completed history builds up.
const STAFF_APPOINTMENTS_LIMIT = 500;

// Colours come from @/lib/appointmentStatus, which also handles legacy
// kebab-case values, so this screen cannot drift from the rest of the app.

// Matches the backend limit on the cancellation remark
const CANCELLATION_REMARK_MAX_LENGTH = 500;

interface CancellationRemarkDialogState {
  appointmentId: string;
  remark: string;
  // True when the appointment is already cancelled and only the remark changes
  isEdit: boolean;
}

export default function StaffDashboardPage() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isLoading } = useAuth();
  const {
    appointments,
    customers,
    vehicles,
    updateAppointment,
    refreshAppointments,
    fetchAppointmentsWithFilters,
  } = useApp();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [remarkDialog, setRemarkDialog] =
    useState<CancellationRemarkDialogState | null>(null);
  const [isSavingRemark, setIsSavingRemark] = useState(false);
  const [featureDialogAppointment, setFeatureDialogAppointment] =
    useState<Appointment | null>(null);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch appointments for the logged-in staff member
  useEffect(() => {
    if (user?.id) {
      fetchAppointmentsWithFilters({
        staffId: user.id,
        limit: STAFF_APPOINTMENTS_LIMIT,
      });
    }
  }, [user?.id, fetchAppointmentsWithFilters]);

  const myAppointments = useMemo(() => {
    if (!user) return [];

    return appointments
      .filter((apt) => apt.staffId === user.id)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [appointments, user]);

  const visibleAppointments = useMemo(() => {
    // "All Status" shows open work only; completed appointments are
    // reachable by selecting the Completed filter explicitly.
    const statusFilteredAppointments = myAppointments.filter((apt) => {
      if (statusFilter === "all") {
        return apt.status !== "COMPLETED";
      }
      return apt.status === statusFilter;
    });

    if (!searchQuery) {
      return statusFilteredAppointments;
    }

    const searchLower = searchQuery.toLowerCase();

    return statusFilteredAppointments.filter((apt) => {
      const customer = customers.find((c) => c.id === apt.customerId);
      const vehicleId = apt.vehicleIds?.[0] || (apt as any).vehicleId;
      const vehicle = vehicles.find((v) => v.id === vehicleId);

      // Safely check search matches - ensure values are strings before calling toLowerCase
      const customerMatch =
        customer?.name && typeof customer.name === "string"
          ? customer.name.toLowerCase().includes(searchLower)
          : false;

      const idMatch =
        apt.id && typeof apt.id === "string"
          ? apt.id.toLowerCase().includes(searchLower)
          : false;

      const plateMatch =
        vehicle?.plateNumber && typeof vehicle.plateNumber === "string"
          ? vehicle.plateNumber.toLowerCase().includes(searchLower)
          : false;

      const matchesSearch = customerMatch || idMatch || plateMatch;
      return matchesSearch;
    });
  }, [myAppointments, searchQuery, statusFilter, customers, vehicles]);

  const selectedStatusOption = statusOptions.find(
    (option) => option.value === statusFilter,
  );
  const appointmentsHeading = selectedStatusOption
    ? `${selectedStatusOption.label} Appointments`
    : "Open Appointments";

  const isAdmin = user?.role === "ADMIN";

  const handleStatusUpdate = async (
    appointmentId: string,
    newStatus: AppointmentStatus,
  ) => {
    // Guard the handler as well as the control: the list may be stale, and the
    // server rejects out-of-window changes with a 403 regardless.
    const appointment = myAppointments.find((a) => a.id === appointmentId);
    if (appointment && isAppointmentStatusLocked(appointment.date, isAdmin)) {
      return;
    }

    // Cancelling needs a reason, so ask for it before saving the status
    if (newStatus === "CANCELLED") {
      openCancellationRemarkDialog(appointmentId);
      return;
    }

    try {
      await updateAppointment(appointmentId, { status: newStatus });
      // Refresh appointments to ensure we have the latest data from the server
      await refreshAppointments();
      // Note: updateAppointment already shows success/error toasts, so we don't need to show another one
    } catch (error) {
      // Error is already handled in updateAppointment with toast
      console.error("Failed to update appointment status:", error);
    }
  };

  const openCancellationRemarkDialog = (appointmentId: string) => {
    const appointment = myAppointments.find((a) => a.id === appointmentId);
    const existingRemark = splitRemarks(appointment?.remarks).cancellationRemark;

    setRemarkDialog({
      appointmentId,
      remark: existingRemark ?? "",
      isEdit: appointment?.status === "CANCELLED",
    });
  };

  const handleSaveCancellationRemark = async () => {
    if (!remarkDialog) return;

    const remark = remarkDialog.remark.trim();
    if (!remark) {
      toast.error("Please enter a cancellation remark");
      return;
    }

    setIsSavingRemark(true);
    try {
      await updateAppointment(
        remarkDialog.appointmentId,
        { status: "CANCELLED" },
        { cancellationRemark: remark },
      );
      await refreshAppointments();
      setRemarkDialog(null);
    } catch (error) {
      // Error is already handled in updateAppointment with toast
      console.error("Failed to save cancellation remark:", error);
    } finally {
      setIsSavingRemark(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate("/login");
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const activeJobsCount = myAppointments.filter((a) =>
    ["CONFIRMED", "IN_PROGRESS"].includes(a.status),
  ).length;

  let saveRemarkButtonLabel = "Cancel Appointment";
  if (isSavingRemark) {
    saveRemarkButtonLabel = "Saving...";
  } else if (remarkDialog?.isEdit) {
    saveRemarkButtonLabel = "Save Remark";
  }

  const renderAppointmentCard = (apt: Appointment) => {
    const customer = customers.find((c) => c.id === apt.customerId);
    const vehicleId = apt.vehicleIds?.[0] || (apt as any).vehicleId;
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    const isSubscriptionAppointment =
      apt.type === "USER_SUBSCRIPTION" || apt.bookingType === "SUBSCRIPTION";
    const bookingLabel = isSubscriptionAppointment
      ? "Subscription"
      : (apt.bookingType ?? "Walk-in");
    // Staff may only change the status until 24 hours after the appointment time.
    const isLocked = isAppointmentStatusLocked(apt.date, isAdmin);
    const isCancelled = apt.status === "CANCELLED";
    const cancellationRemark = splitRemarks(apt.remarks).cancellationRemark;
    const isUserSubscription = apt.type === "USER_SUBSCRIPTION";
    const featuresUsed = apt.featuresUsed || [];
    const canManageFeatures =
      isUserSubscription &&
      !isLocked &&
      !FEATURE_LOCKED_STATUSES.includes(apt.status);

    return (
      <Card key={apt.id} className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h3 className="font-semibold text-base">
                  {apt.customerName || customer?.name || "N/A"}
                </h3>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(apt.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {formatTime(apt.date)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canManageFeatures && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFeatureDialogAppointment(apt)}
                  >
                    <CheckSquare className="mr-1 h-3.5 w-3.5" />
                    Manage Features
                  </Button>
                )}
                <Select
                  value={apt.status}
                  onValueChange={(value) =>
                    handleStatusUpdate(apt.id, value as AppointmentStatus)
                  }
                  disabled={apt.status === "COMPLETED" || isLocked}
                >
                  <SelectTrigger
                    className={cn(
                      "w-[180px]",
                      getStatusBadgeClasses(apt.status),
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-2 text-sm">
                <CarIcon className="h-4 w-4 text-muted-foreground" />
                <span>
                  {vehicle?.make} {vehicle?.model} - {vehicle?.plateNumber}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span>
                  {apt.serviceName ||
                    (apt.type ? apt.type.replace(/_/g, " ") : "Service")}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Booking:</span>
                <span>{bookingLabel}</span>
              </div>
              {isSubscriptionAppointment ? (
                <div className="flex items-center gap-2 text-sm col-span-2">
                  <span className="text-muted-foreground">Payment:</span>
                  <span>Included in subscription</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Payment:</span>
                  <span>{apt.paymentMode || "N/A"}</span>
                </div>
              )}
              {isUserSubscription && (
                <div className="flex flex-wrap items-center gap-2 text-sm col-span-2">
                  <span className="text-muted-foreground">Features Used:</span>
                  {featuresUsed.length > 0 ? (
                    featuresUsed.map((feature) => (
                      <Badge
                        key={feature.featureId}
                        variant="secondary"
                        className={feature.isExtra ? "bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300" : undefined}
                      >
                        {feature.featureName}
                        {feature.isExtra && " · Extra"}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted-foreground">None recorded</span>
                  )}
                </div>
              )}
              {apt.address && (
                <div className="flex items-center gap-2 text-sm col-span-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{apt.address}</span>
                </div>
              )}
              {!isSubscriptionAppointment && apt.amount !== null && (
                <div className="flex items-center gap-2 text-sm col-span-2">
                  <span className="font-medium">
                    Amount:{" "}
                    {formatCurrency((apt.amount ?? 0) - (apt.discount ?? 0))}
                  </span>
                  {(apt.discount || 0) > 0 && (
                    <span className="text-xs text-muted-foreground">
                      (Discount: {formatCurrency(apt.discount || 0)})
                    </span>
                  )}
                </div>
              )}
            </div>

            {(apt as any).specialInstructions && (
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm">
                  <span className="font-medium">Special Instructions:</span>{" "}
                  {(apt as any).specialInstructions}
                </p>
              </div>
            )}

            {isCancelled && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm whitespace-pre-wrap break-words">
                    <span className="font-medium">Cancellation Remark:</span>{" "}
                    {cancellationRemark || (
                      <span className="text-muted-foreground">
                        No remark added
                      </span>
                    )}
                  </p>
                  {!isLocked && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => openCancellationRemarkDialog(apt.id)}
                    >
                      <Pencil className="mr-1 h-3.5 w-3.5" />
                      {cancellationRemark ? "Edit" : "Add"}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <CarIcon className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold">WeClean Staff Portal</h1>
              <p className="text-xs text-muted-foreground">
                Welcome, {user.name}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout} size="sm">
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      <main className="container mx-auto p-6 space-y-6">
        {/* Time Tracking Widget */}
        <StaffTimeTrackingProvider>
          <TimeTrackingWidget />
        </StaffTimeTrackingProvider>

        {/* Stats */}
        <div className="grid gap-5">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <p className="text-sm font-medium text-muted-foreground">
                Active Jobs
              </p>
              <div className="flex items-baseline gap-2 mt-2">
                <h3 className="text-2xl font-semibold">{activeJobsCount}</h3>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Confirmed & In Progress
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              My Appointments
            </CardTitle>
            <CardDescription>
              View and manage your assigned appointments
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by customer name, booking ref, or plate number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {visibleAppointments.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">{appointmentsHeading}</h2>
            <div className="grid gap-4">
              {visibleAppointments.map(renderAppointmentCard)}
            </div>
          </div>
        )}

        {visibleAppointments.length === 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No appointments found</p>
            </CardContent>
          </Card>
        )}
      </main>

      <Dialog
        open={remarkDialog !== null}
        onOpenChange={(open) => {
          if (!open && !isSavingRemark) {
            setRemarkDialog(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {remarkDialog?.isEdit
                ? "Cancellation Remark"
                : "Cancel Appointment"}
            </DialogTitle>
            <DialogDescription>
              {remarkDialog?.isEdit
                ? "Add or update the reason this booking was cancelled."
                : "Enter the reason for cancelling this booking. The appointment will be marked as Cancelled."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancellation-remark">Reason for cancellation</Label>
            <Textarea
              id="cancellation-remark"
              placeholder="e.g. Customer not available at location"
              value={remarkDialog?.remark ?? ""}
              maxLength={CANCELLATION_REMARK_MAX_LENGTH}
              onChange={(e) =>
                setRemarkDialog((prev) =>
                  prev ? { ...prev, remark: e.target.value } : prev,
                )
              }
              rows={4}
              autoFocus
            />
            <p className="text-xs text-muted-foreground text-right">
              {remarkDialog?.remark.length ?? 0}/{CANCELLATION_REMARK_MAX_LENGTH}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRemarkDialog(null)}
              disabled={isSavingRemark}
            >
              Close
            </Button>
            <Button
              onClick={handleSaveCancellationRemark}
              disabled={isSavingRemark || !remarkDialog?.remark.trim()}
            >
              {saveRemarkButtonLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StaffFeatureUsageDialog
        appointment={featureDialogAppointment}
        onOpenChange={(open) => {
          if (!open) {
            setFeatureDialogAppointment(null);
          }
        }}
        onSaved={refreshAppointments}
      />
    </div>
  );
}
